import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { Send, LockOutlined, AddComment, History } from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import PageWithHeader from "../common/PageWithHeader";
import {
  Lookup,
  StoredMessage,
  useGetAiAssistantConversationsQuery,
  useGetAiAssistantSourcesQuery,
  useLazyGetAiAssistantConversationQuery,
  usePostAiAssistantChatMutation,
} from "../../redux/aiAssistantApi";
import {
  formatWhen,
  LookupChips,
  LookupDetail,
  SYSTEM_STYLE,
  SystemBadge,
} from "./LookupPanel";

const STARTERS = [
  "What did we sell today?",
  "Which purchase orders are approved in VMS but unpaid?",
  "Compare this month's sales between Odoo and our sales log",
  "Which requisitions are still waiting for approval?",
];

const extractText = (node: React.ReactNode): string =>
  React.Children.toArray(node)
    .map((c: any) =>
      typeof c === "string" ? c : extractText(c?.props?.children),
    )
    .join("");

const Answer: React.FC<{ content: string }> = ({ content }) => (
  <Box
    sx={{
      fontSize: 14.5,
      lineHeight: 1.55,
      "& table": { borderCollapse: "collapse", my: 1, fontSize: 13 },
      "& th, & td": { border: "1px solid #dde", p: "6px 10px", textAlign: "left" },
      "& th": { bgcolor: "#f3f5fa" },
      "& p": { my: 0.8 },
      "& ul": { my: 0.5, pl: 3 },
      "& pre": { bgcolor: "#10172a", color: "#e6e9f5", p: 1.5, borderRadius: 1, overflow: "auto" },
    }}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        blockquote: ({ children }) => {
          const warn = extractText(children).trim().startsWith("⚠");
          return (
            <Box
              sx={{
                bgcolor: warn ? "#fff7d6" : "#f3f5fa",
                borderLeft: `4px solid ${warn ? "#f2b600" : "#9aa5c0"}`,
                borderRadius: 1,
                px: 2,
                py: 0.5,
                my: 1.5,
              }}
            >
              {children}
            </Box>
          );
        },
        table: ({ children }) => (
          <Box sx={{ overflowX: "auto" }}>
            <table>{children}</table>
          </Box>
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  </Box>
);

const SourceLine: React.FC<{
  lookups: Lookup[];
  onSelect: (id: string) => void;
}> = ({ lookups, onSelect }) => {
  const used = lookups.filter((l) => !l.error && l.count > 0);
  if (!used.length) return null;
  return (
    <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.8, alignItems: "center" }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: "#555" }}>
        SOURCES
      </Typography>
      {used.map((l) => (
        <Box
          key={l.id}
          onClick={() => onSelect(l.id)}
          sx={{
            cursor: "pointer",
            border: "1px solid #d5d9e6",
            borderRadius: 4,
            px: 1,
            py: 0.3,
            fontSize: 12,
            bgcolor: "#fff",
            display: "flex",
            gap: 0.7,
            alignItems: "center",
          }}
        >
          <SystemBadge system={l.system} small />
          {l.record.length > 40 ? l.record.slice(0, 40) + "…" : l.record} ·{" "}
          {l.asOf && l.system === "GOOGLE_SHEETS"
            ? `updated ${formatWhen(l.asOf)}`
            : "live"}
        </Box>
      ))}
    </Box>
  );
};

const AiAssistant: React.FC = () => {
  const { data: sourceInfo } = useGetAiAssistantSourcesQuery();
  const { data: history, refetch: refetchHistory } =
    useGetAiAssistantConversationsQuery();
  const [loadConversation] = useLazyGetAiAssistantConversationQuery();
  const [chat, { isLoading }] = usePostAiAssistantChatMutation();

  const [messages, setMessages] = useState<StoredMessage[]>([]);
  const [conversationId, setConversationId] = useState<string>();
  const [input, setInput] = useState("");
  const [error, setError] = useState<string>();
  const [tab, setTab] = useState(0);
  const [selectedId, setSelectedId] = useState<string>();
  const [showQueries, setShowQueries] = useState<Record<number, boolean>>({});
  const [showHistory, setShowHistory] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  // Lookup ids repeat per answer (L1, L2...), so keep every lookup with a unique key
  const allLookups = useMemo(
    () =>
      messages.flatMap((m, mi) =>
        (m.lookups ?? []).map((l) => ({ ...l, id: `${mi}-${l.id}` })),
      ),
    [messages],
  );
  const selected =
    allLookups.find((l) => l.id === selectedId) ??
    allLookups.filter((l) => !l.error)[0];

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  const send = async (text: string) => {
    const question = text.trim();
    if (!question || isLoading) return;
    setError(undefined);
    setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", content: question, createdAt: new Date().toISOString() },
    ]);
    try {
      const res = await chat({ question, conversationId }).unwrap();
      setConversationId(res.conversationId);
      setMessages((m) => {
        const next: StoredMessage[] = [
          ...m,
          {
            role: "assistant",
            content: res.content,
            createdAt: new Date().toISOString(),
            lookups: res.lookups,
            followUps: res.followUps,
          },
        ];
        const first = res.lookups.find((l) => !l.error);
        if (first) setSelectedId(`${next.length - 1}-${first.id}`);
        return next;
      });
      refetchHistory();
    } catch (e: any) {
      setError(
        e?.data?.error?.message ??
          "The assistant could not answer. Please try again.",
      );
    }
  };

  const reset = () => {
    setMessages([]);
    setConversationId(undefined);
    setSelectedId(undefined);
    setError(undefined);
  };

  const open = async (id: string) => {
    const res = await loadConversation(id).unwrap();
    setMessages(res.messages);
    setConversationId(id);
    setSelectedId(undefined);
    setShowHistory(false);
  };

  const lookupsFor = (mi: number) =>
    (messages[mi].lookups ?? []).map((l) => ({ ...l, id: `${mi}-${l.id}` }));

  return (
    <PageWithHeader bgColor="#f4f6fb">
      <Box sx={{ px: 3, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "3px solid #f6c60f", bgcolor: "#fff" }}>
        <Box>
          <Typography sx={{ fontWeight: 800, letterSpacing: 1, color: "#12356b" }}>
            C SUITE AI ASSISTANT
          </Typography>
          <Typography variant="body2" color="text.secondary">
            One question across Odoo, VMS and Google Sheets. Every answer shows its source.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button startIcon={<History />} variant="outlined" onClick={() => setShowHistory((s) => !s)}>
            History
          </Button>
          <Button startIcon={<AddComment />} variant="outlined" onClick={reset}>
            New chat
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: 3, py: 1, bgcolor: "#fff", display: "flex", gap: 1.5, alignItems: "center", overflowX: "auto", borderBottom: "1px solid #e3e6ef" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#2e7d32", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
          <LockOutlined fontSize="small" /> READ-ONLY
        </Box>
        {(sourceInfo?.sources ?? []).map((s) => (
          <Box
            key={s.key}
            title={s.connected ? "Connected" : "Not connected yet"}
            sx={{ display: "flex", gap: 1, alignItems: "center", border: "1px solid #dde", borderRadius: 2, px: 1.2, py: 0.6, flexShrink: 0, opacity: s.connected ? 1 : 0.45 }}
          >
            <Box sx={{ bgcolor: SYSTEM_STYLE[s.key]?.color, color: "#fff", borderRadius: 1, width: 28, height: 28, display: "grid", placeItems: "center", fontSize: 12, fontWeight: 700 }}>
              {SYSTEM_STYLE[s.key]?.short}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.1 }}>{s.label}</Typography>
              <Typography sx={{ fontSize: 11, color: "#666" }}>
                {s.connected ? s.description : "Not connected yet"}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {showHistory && (
        <Box sx={{ px: 3, py: 1, bgcolor: "#fff", borderBottom: "1px solid #e3e6ef", display: "flex", gap: 1, flexWrap: "wrap" }}>
          {(history ?? []).length === 0 && (
            <Typography variant="body2" color="text.secondary">No earlier chats yet.</Typography>
          )}
          {(history ?? []).map((h) => (
            <Button key={h.id} size="small" variant="outlined" onClick={() => open(h.id)}>
              {h.channel === "whatsapp" ? "📱 " : ""}
              {h.title ?? "Chat"}
            </Button>
          ))}
        </Box>
      )}

      <Box sx={{ display: { xs: "block", md: "flex" }, height: { md: "calc(100vh - 250px)" }, minHeight: 480 }}>
        <Box sx={{ flex: "0 0 46%", display: "flex", flexDirection: "column", p: 2, minHeight: 0 }}>
          <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
            <Box sx={{ bgcolor: "#e4ebf7", borderRadius: 3, p: 2, mb: 2, fontSize: 14 }}>
              <b>Ask one question across WestProp's systems.</b> I read Odoo, VMS and
              Google Sheets and show the system, the record, its date and the exact
              lookup under every answer.
              <br />
              <br />
              When two systems disagree, or a file is out of date, I say so. I can
              only read; I never change or delete anything.
            </Box>
            {messages.length === 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {STARTERS.map((s) => (
                  <Button key={s} size="small" variant="outlined" sx={{ borderRadius: 5, textTransform: "none" }} onClick={() => send(s)}>
                    {s}
                  </Button>
                ))}
              </Box>
            )}

            {messages.map((m, mi) =>
              m.role === "user" ? (
                <Box key={mi} sx={{ display: "flex", justifyContent: "flex-end", my: 2 }}>
                  <Box sx={{ bgcolor: "#1b3f8f", color: "#fff", px: 2, py: 1.2, borderRadius: 3, maxWidth: "85%" }}>
                    {m.content}
                  </Box>
                </Box>
              ) : (
                <Box key={mi} sx={{ my: 2 }}>
                  {(m.lookups?.length ?? 0) > 0 && (
                    <Box sx={{ border: "1px solid #dde", bgcolor: "#fff", borderRadius: 2, p: 1.2, mb: 1.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                        <span>
                          {m.lookups!.length} lookups in{" "}
                          {Array.from(new Set(m.lookups!.map((l) => SYSTEM_STYLE[l.system]?.label))).join(", ")}
                        </span>
                        <Button size="small" onClick={() => setShowQueries((s) => ({ ...s, [mi]: !s[mi] }))}>
                          {showQueries[mi] ? "Hide the queries" : "Show the queries"}
                        </Button>
                      </Box>
                      {showQueries[mi] &&
                        lookupsFor(mi).map((l) => (
                          <Box key={l.id} onClick={() => setSelectedId(l.id)} sx={{ cursor: "pointer", mt: 1, borderLeft: `4px solid ${SYSTEM_STYLE[l.system]?.color}`, bgcolor: "#f7f8fb", p: 1 }}>
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", fontSize: 13 }}>
                              <SystemBadge system={l.system} small /> {l.record}
                              <span style={{ color: "#777" }}>
                                {l.error ? "failed" : `${l.count} records`}
                              </span>
                            </Box>
                            <Box component="pre" sx={{ m: 0, mt: 0.5, fontSize: 11.5, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                              {l.query}
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  )}
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#12356b", mb: 0.5 }}>
                    WESTPROP AI ASSISTANT
                  </Typography>
                  <Box sx={{ bgcolor: "#fff", border: "1px solid #dde", borderRadius: 3, p: 2 }}>
                    <Answer content={m.content} />
                  </Box>
                  <SourceLine lookups={lookupsFor(mi)} onSelect={setSelectedId} />
                  {mi === messages.length - 1 && (m.followUps?.length ?? 0) > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
                      {m.followUps!.map((f) => (
                        <Button key={f} size="small" variant="outlined" sx={{ borderRadius: 5, textTransform: "none" }} onClick={() => send(f)}>
                          {f}
                        </Button>
                      ))}
                    </Box>
                  )}
                </Box>
              ),
            )}
            {isLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, color: "#555", my: 2 }}>
                <CircularProgress size={18} /> Looking this up across the systems…
              </Box>
            )}
            {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
            <div ref={endRef} />
          </Box>

          <Box sx={{ mt: 1.5, border: "1px solid #ccd", borderRadius: 3, bgcolor: "#fff", display: "flex", alignItems: "center", px: 2, py: 0.5 }}>
            <InputBase
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask about sales, spend, POs"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
            />
            <IconButton onClick={() => send(input)} disabled={isLoading || !input.trim()} sx={{ bgcolor: "#1b3f8f", color: "#fff", "&:hover": { bgcolor: "#16336f" }, "&.Mui-disabled": { bgcolor: "#9db0d8", color: "#fff" } }}>
              <Send fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="caption" sx={{ textAlign: "center", mt: 0.5, color: "#666" }}>
            AI can make mistakes. Check the source records on the right before acting.
          </Typography>
        </Box>

        <Box sx={{ flex: 1, p: 2, overflowY: "auto", borderLeft: { md: "1px solid #e3e6ef" } }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5 }}>
            <Tab label={`Sources${allLookups.length ? ` (${allLookups.length})` : ""}`} />
            <Tab label="WhatsApp" />
            <Tab label="How it connects" />
          </Tabs>

          {tab === 0 &&
            (allLookups.length === 0 ? (
              <Typography color="text.secondary">
                Records behind each answer will appear here.
              </Typography>
            ) : (
              <>
                <LookupChips lookups={allLookups} selectedId={selected?.id} onSelect={setSelectedId} />
                {selected && <LookupDetail lookup={selected} />}
              </>
            ))}

          {tab === 1 && (
            <Box sx={{ fontSize: 14, lineHeight: 1.6 }}>
              <Typography fontWeight={700} gutterBottom>Ask from WhatsApp</Typography>
              Message the WestProp WhatsApp number from an approved phone and the
              assistant answers the same questions with the same sources listed under
              the answer. Send <b>new</b> to start a fresh conversation. WhatsApp chats
              also appear under History here.
              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#fff", border: "1px solid #dde", borderRadius: 2 }}>
                Only phone numbers on the approved list receive answers.
              </Box>
            </Box>
          )}

          {tab === 2 && (
            <Box sx={{ fontSize: 14, lineHeight: 1.6 }}>
              <Typography fontWeight={700} gutterBottom>How it connects</Typography>
              <ul>
                <li><b>VMS</b>: read-only queries on an approved list of collections (requisitions, POs, payments, finance, projects).</li>
                <li><b>Odoo</b>: read-only search and totals on CRM, sales, invoices and HR through the existing Odoo integration.</li>
                <li><b>Google Sheets</b>: live reads of sheets shared with the assistant's service account, with last-updated time and editor.</li>
                <li><b>Pastel, Candy, Microsoft 365</b>: not connected yet.</li>
              </ul>
              The assistant has no write or delete access to any system. Every question and
              lookup is logged.
            </Box>
          )}
        </Box>
      </Box>
    </PageWithHeader>
  );
};

export default AiAssistant;
